import AccountIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import {
	AppBar,
	Avatar,
	Box,
	Button,
	IconButton,
	Menu,
	MenuItem,
	Modal,
	Toolbar,
	Tooltip,
	Typography,
} from "@mui/material";
import { Fragment, MouseEvent, useState } from "react";
import { IUseKeycloak, useKeycloak } from "react-keycloak-manager";
import { Link } from "react-router-dom";
import ProtectedElement from "../helpers/ProtectElement";

import SettingsIcon from "@mui/icons-material/Settings";
import { useTranslation } from "react-i18next";
import { ThemeDrawer } from "react-vevy-mui-theme";

export interface IMenuPage {
	link: string;
	title: string;
}

export default function MainMenu({
	title,
	pages,
}: {
	title: string;
	pages: IMenuPage[];
}) {
	const useKeyCloak = useKeycloak();

	return (
		<AppBar position='static'>
			<LargeToolbar title={title} pages={pages} keyCloak={useKeyCloak} />
			<SmallToolbar title={title} pages={pages} keyCloak={useKeyCloak} />
		</AppBar>
	);
}

function SmallToolbar({
	title,
	pages,
	keyCloak,
}: {
	title: string;
	pages: IMenuPage[];
	keyCloak: IUseKeycloak;
}) {
	const { isAuthenticated, authenticatedUser } = keyCloak;
	return (
		<Toolbar disableGutters sx={{ display: { xs: "flex", md: "none" } }}>
			<MenuIconButton pages={pages} keyCloak={keyCloak} />
			<Box sx={{ flexGrow: 1, flex: 1 }}>
				<Typography color='common.white' sx={{ mx: 2 }}>
					{title}
				</Typography>
			</Box>
			<Box sx={{ flexGrow: 0, mr: 2 }}>
				<Typography
					color='common.white'
					sx={{ display: { xs: "none", md: "flex" } }}
				>
					{isAuthenticated ? authenticatedUser?.profile.name : ""}
				</Typography>
				<LoginButtonIcon keyCloak={keyCloak} />
				<ThemesDrawer />
			</Box>
		</Toolbar>
	);
}

function LargeToolbar({
	title,
	pages,
	keyCloak,
}: {
	title: string;
	pages: IMenuPage[];
	keyCloak: IUseKeycloak;
}) {
	return (
		<Toolbar disableGutters sx={{ display: { xs: "none", md: "flex" } }}>
			<Typography color='common.white' sx={{ mx: 2 }}>
				{title}
			</Typography>
			<HorizzontalButtons
				isAuthenticated={keyCloak.isAuthenticated}
				menu={pages}
			/>
			<Box sx={{ flexGrow: 0, mr: 2 }}>
				<Typography color='common.white'>
					{keyCloak.isAuthenticated
						? keyCloak.authenticatedUser?.profile.name
						: ""}
					<LoginButtonIcon keyCloak={keyCloak} />
					<ThemesDrawer />
				</Typography>
			</Box>
		</Toolbar>
	);
}

function HorizzontalButtons({
	isAuthenticated,
	menu,
}: {
	isAuthenticated: boolean;
	menu: IMenuPage[];
}) {
	return (
		<Box
			sx={{
				flexGrow: 1,
				ml: 2,
				display: "flex",
				justifyContent: "flex-start",
			}}
		>
			<ProtectedElement enabled={() => isAuthenticated}>
				<Fragment>
					{menu.map((m, i) => {
						return (
							<Button
								key={`HB-${i}`}
								sx={{ display: "block" }}
								color='inherit'
								href={m.link}
								onClick={() => {}}
							>
								<Typography color='common.white'>{m.title}</Typography>
							</Button>
						);
					})}
				</Fragment>
			</ProtectedElement>
		</Box>
	);
}

function MenuIconButton({
	pages,
	keyCloak,
}: {
	pages: IMenuPage[];
	keyCloak: IUseKeycloak;
}) {
	const [anchorElPages, setAnchorElPages] = useState<null | HTMLElement>(null);
	function handleOpenMenu(event: MouseEvent<HTMLElement>): void {
		setAnchorElPages(event.currentTarget);
	}
	return (
		<>
			<IconButton
				id='menu-icon-button'
				size='large'
				edge='start'
				aria-label='menu'
				aria-controls='menu-appbar'
				aria-haspopup='true'
				onClick={handleOpenMenu}
				sx={{ ml: 2, color: "common.white" }}
			>
				<MenuIcon />
			</IconButton>
			<ProtectedElement enabled={() => keyCloak.isAuthenticated}>
				<Menu
					id='menu-appbar'
					aria-labelledby='menu-icon-button'
					open={Boolean(anchorElPages)}
					onClose={() => {
						setAnchorElPages(null);
					}}
					anchorEl={anchorElPages}
					keepMounted
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "left",
					}}
					sx={{
						display: { lg: "none", md: "none" },
						bgcolor: "primary",
					}}
				>
					{pages.map((m, i) => {
						return (
							<MenuItem
								key={`MIC-${i}`}
								component={Link}
								to={m.link}
								onClick={() => {
									setAnchorElPages(null);
								}}
							>
								<Typography sx={{ textTransform: "uppercase", fontSize: 12 }}>
									{m.title}
								</Typography>
							</MenuItem>
						);
					})}
				</Menu>
			</ProtectedElement>
		</>
	);
}

function LoginButtonIcon({ keyCloak }: { keyCloak: IUseKeycloak }) {
	const [anchorElLogin, setAnchorElLogin] = useState<null | HTMLElement>(null);
	const [showInfo, setShowInfo] = useState<boolean>(false);

	async function handleOpenMenu(event: MouseEvent<HTMLElement>): Promise<void> {
		if (keyCloak.isAuthenticated) setAnchorElLogin(event.currentTarget);
		else
			await keyCloak.login({
				extraQueryParams: { kc_idp_hint: "" },
			});
	}

	return (
		<Fragment>
			<Tooltip title={keyCloak.isAuthenticated ? "" : "Login"}>
				<IconButton
					size='large'
					aria-label='account of current user'
					aria-controls='menu-appbar'
					aria-haspopup='true'
					onClick={handleOpenMenu}
					color='inherit'
				>
					{keyCloak.isAuthenticated ? (
						<Avatar
							src={keyCloak.authenticatedUser?.profile.picture}
							sx={{ width: 24, height: 24 }}
						/>
					) : (
						<AccountIcon sx={{ color: "common.white" }} />
					)}
				</IconButton>
			</Tooltip>
			<ProtectedElement enabled={() => keyCloak.isAuthenticated}>
				<Menu
					id='menu-appbar'
					aria-labelledby='menu-icon-button'
					open={Boolean(anchorElLogin)}
					onClose={() => {
						setAnchorElLogin(null);
					}}
					anchorEl={anchorElLogin}
					keepMounted
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "left",
					}}
				>
					<MenuItem
						key='logout'
						onClick={() => {
							setAnchorElLogin(null);
							keyCloak.logout();
						}}
					>
						<Typography sx={{ textTransform: "uppercase", fontSize: 12 }}>
							Logout
						</Typography>
					</MenuItem>
					<MenuItem
						href='https://www.vevy.com'
						target='_blank'
						component='a'
						onClick={() => setAnchorElLogin(null)}
					>
						<Typography sx={{ textTransform: "uppercase", fontSize: 12 }}>
							Privacy
						</Typography>
					</MenuItem>
					<MenuItem
						onClick={() => {
							var ShowInfoEvent = new Event("showInfo");
							window.dispatchEvent(ShowInfoEvent);
							setAnchorElLogin(null);
						}}
					>
						<Typography sx={{ textTransform: "uppercase", fontSize: 12 }}>
							Info
						</Typography>
					</MenuItem>
				</Menu>
			</ProtectedElement>
		</Fragment>
	);
}
function ThemesDrawer() {
	const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
	const { t } = useTranslation();

	function handleOpenMenu(): void {
		setDrawerOpen(true);
	}

	return (
		<Fragment>
			<Tooltip title={t("settings")}>
				<IconButton
					size='large'
					aria-label='account of current user'
					aria-controls='menu-appbar'
					aria-haspopup='true'
					onClick={handleOpenMenu}
				>
					<SettingsIcon sx={{ color: "common.white" }} />
				</IconButton>
			</Tooltip>
			<ThemeDrawer
				anchor='right'
				isOpen={drawerOpen}
				setOpen={(v) => setDrawerOpen(v)}
			/>
		</Fragment>
	);
}
